import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { ReportProblemOutlined } from '@mui/icons-material'
import { Card, CardContent, CardHeader, Chip, Divider, Link as MuiLink, Stack, Typography } from '@mui/material'

import { MarketArchetype } from '../../../../api/markets'
import { languageServiceNameResolver } from '../../../../components/MarketLevelBlueprintSearch/marketLevelBlueprintGrouping'
import {
  BlueprintEventTypeMismatch,
  blueprintsWithAcceptedOddCases,
  blueprintsWithEventTypeMismatches,
  blueprintsWithoutAcceptedEventTypes,
} from '../../helpers/oddEventTypes'

type MarketBlueprintOddTypesCardProps = {
  marketArchetypes: MarketArchetype[]
}

/**
 * Lists every Market Level Blueprint whose active events carry an event type the blueprint does not
 * accept. Nothing on the backend blocks those events, so this is the view that makes them fixable.
 *
 * Events an analyst accepted as exceptions are done with, so the blueprints that have them are kept
 * behind "Show", the same as on a blueprint's own Odd cases card. Opening a blueprint shows which
 * events they are.
 */
export const MarketBlueprintOddTypesCard: FC<MarketBlueprintOddTypesCardProps> = ({ marketArchetypes }) => {
  const { t } = useTranslation()
  const [showAccepted, setShowAccepted] = useState(false)
  const blueprints = blueprintsWithEventTypeMismatches(marketArchetypes)
  const acceptedBlueprints = blueprintsWithAcceptedOddCases(marketArchetypes)
  const uncuratedCount = blueprintsWithoutAcceptedEventTypes(marketArchetypes).length
  const eventCount = blueprints.reduce((total, blueprint) => total + blueprint.mismatchedEventCount, 0)
  const acceptedEventCount = acceptedBlueprints.reduce((total, blueprint) => total + blueprint.mismatchedEventCount, 0)

  return (
    <Card sx={{ marginTop: 3 }}>
      <CardHeader
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            <ReportProblemOutlined color={blueprints.length > 0 ? 'warning' : 'disabled'} />
            <span>{t('internal-live-events:odd_cases')}</span>
            {blueprints.length > 0 && <Chip size="small" color="warning" sx={{ margin: 0 }} label={eventCount} />}
          </Stack>
        }
      />
      <Divider />
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('internal-live-events:odd_types_helper')}
          </Typography>

          {blueprints.length === 0 ? (
            <Typography variant="body1">
              {acceptedEventCount > 0 ? t('internal-live-events:odd_types_none_to_review') : t('internal-live-events:odd_types_none')}
            </Typography>
          ) : (
            <BlueprintOddCaseList blueprints={blueprints} />
          )}

          {showAccepted && acceptedBlueprints.length > 0 && (
            <Stack spacing={1.5}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 'bold', letterSpacing: '.07em', textTransform: 'uppercase', fontSize: '10.5px' }}
              >
                {t('internal-live-events:odd_cases_accepted_section')}
              </Typography>
              <BlueprintOddCaseList blueprints={acceptedBlueprints} accepted />
            </Stack>
          )}

          {acceptedEventCount > 0 && (
            <Typography variant="body2" color="text.secondary">
              {showAccepted
                ? t('internal-live-events:odd_cases_accepted_shown', { count: acceptedEventCount })
                : t('internal-live-events:odd_cases_accepted_hidden', { count: acceptedEventCount })}{' '}
              ·{' '}
              <MuiLink component="button" type="button" onClick={() => setShowAccepted((current) => !current)} sx={{ verticalAlign: 'baseline' }}>
                {showAccepted ? t('internal-live-events:odd_cases_hide_accepted') : t('internal-live-events:odd_cases_show_accepted')}
              </MuiLink>
            </Typography>
          )}

          {uncuratedCount > 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('internal-live-events:odd_types_uncurated', { count: uncuratedCount })}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}

/** One line per blueprint: its name, how many events, and the types behind them. */
const BlueprintOddCaseList: FC<{ blueprints: BlueprintEventTypeMismatch[]; accepted?: boolean }> = ({ blueprints, accepted }) => {
  const { t } = useTranslation()

  return (
    <Stack spacing={1.5}>
      {blueprints.map(({ marketArchetype, mismatchedTypeIds, mismatchedEventCount }) => (
        <Stack key={marketArchetype.id} spacing={0.25}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              <MuiLink component={Link} to={`/internal/live-events-tracking/market-level-blueprints/overview/${marketArchetype.id}`}>
                {marketArchetype.names.en}
              </MuiLink>
            </Typography>
            <Chip
              size="small"
              color={accepted ? 'default' : 'warning'}
              sx={{ margin: 0 }}
              label={t('internal-live-events:event_count', { count: mismatchedEventCount })}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {mismatchedTypeIds
              .map((eventType) =>
                t('internal-live-events:odd_types_entry', {
                  type: languageServiceNameResolver.eventTypeName(eventType.typeId) || eventType.typeId,
                  count: eventType.count,
                })
              )
              .join(', ')}
          </Typography>
        </Stack>
      ))}
    </Stack>
  )
}
